import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from './auth.service';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, take, timeout } from 'rxjs/operators';
import { FeatureSettingsService, FeatureFlags } from '../services/feature-settings.service';

/**
 * Auth Guard - Protects routes that require authentication
 *
 * Flow:
 * 1. Wait for authInitialized$ to be true (auth state fully resolved)
 * 2. Take the current value from currentUser$
 * 3. If user exists AND status is active → allow access
 * 4. If no user or not active → redirect to login with returnUrl
 */
export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.authInitialized$.pipe(
    filter(initialized => initialized === true),
    take(1),
    switchMap(() => authService.currentUser$.pipe(take(1))),
    timeout(10000),
    map(user => {
      if (user && user.status === 'active') {
        console.log('[authGuard] User authenticated and active, allowing access', {
          userId: user.id,
          route: state.url
        });
        return true;
      } else {
        console.log('[authGuard] No user or user not active, redirecting to login', {
          returnUrl: state.url,
          status: user?.status
        });
        if (user && user.status !== 'active') {
          authService.logout();
        }
        router.navigate(['/auth/login'], {
          queryParams: { returnUrl: state.url },
        });
        return false;
      }
    }),
    catchError((error) => {
      console.error('[authGuard] Error or timeout', error);
      router.navigate(['/auth/login'], {
        queryParams: { returnUrl: state.url },
      });
      return of(false);
    })
  );
};

/**
 * Role Guard - Protects routes that require specific roles
 *
 * Flow:
 * 1. Wait for auth to be initialized
 * 2. Take the current value from currentUser$
 * 3. Check user is active AND has one of the allowed roles
 * 4. If role matches → allow access
 * 5. If role doesn't match → redirect to dashboard
 * 6. If no user or not active → redirect to login
 */
export const roleGuard = (allowedRoles: string[]): CanActivateFn => {
  return (route, state) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    return authService.authInitialized$.pipe(
      filter(initialized => initialized === true),
      take(1),
      switchMap(() => authService.currentUser$.pipe(take(1))),
      timeout(10000),
      map(user => {
        if (!user || user.status !== 'active') {
          console.log('[roleGuard] No user or user not active, redirecting to login');
          if (user && user.status !== 'active') {
            authService.logout();
          }
          router.navigate(['/auth/login'], {
            queryParams: { returnUrl: state.url },
          });
          return false;
        }

        const userRole = user.role;
        const isAllowed = !!userRole && allowedRoles.includes(userRole);

        if (!isAllowed) {
          console.warn('[roleGuard] Access denied', {
            url: state.url,
            userRole,
            allowedRoles,
          });
          router.navigate(['/dashboard']);
          return false;
        }

        console.log('[roleGuard] Access granted', {
          url: state.url,
          userRole,
        });
        return true;
      }),
      catchError((error) => {
        console.error('[roleGuard] Error or timeout', error);
        router.navigate(['/auth/login'], {
          queryParams: { returnUrl: state.url },
        });
        return of(false);
      })
    );
  };
};

/**
 * Class Guard - Ensures teachers have assigned classes and parents have children linked.
 * For owner/admin/manager roles, always allows access.
 * Must be used AFTER authGuard (user is guaranteed to exist).
 */
export const classGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.authInitialized$.pipe(
    filter(initialized => initialized === true),
    take(1),
    switchMap(() => authService.currentUser$.pipe(take(1))),
    timeout(10000),
    map(user => {
      if (!user) {
        router.navigate(['/auth/login']);
        return false;
      }

      // Owner, admin, manager always pass
      if (user.role === 'owner' || user.role === 'admin' || user.role === 'manager') {
        return true;
      }

      // Teachers must have assigned classes
      if (user.role === 'teacher') {
        if (!user.assignedClasses || user.assignedClasses.length === 0) {
          console.warn('[classGuard] Teacher has no assigned classes', { userId: user.id });
          router.navigate(['/dashboard']);
          return false;
        }
        return true;
      }

      // Parents must have children linked
      if (user.role === 'parent') {
        if (!user.children || user.children.length === 0) {
          console.warn('[classGuard] Parent has no children linked', { userId: user.id });
          router.navigate(['/dashboard']);
          return false;
        }
        return true;
      }

      return true;
    }),
    catchError((error) => {
      console.error('[classGuard] Error or timeout', error);
      router.navigate(['/dashboard']);
      return of(false);
    })
  );
};

/**
 * Feature Guard - Blocks routes when the corresponding feature flag is disabled.
 * Redirects to /dashboard when the feature is not enabled by the school admin.
 */
export const featureGuard = (featureKey: keyof Omit<FeatureFlags, 'updatedAt' | 'updatedBy'>): CanActivateFn => {
  return () => {
    const featureService = inject(FeatureSettingsService);
    const router = inject(Router);

    return featureService.isFeatureEnabled(featureKey).pipe(
      map(enabled => {
        if (enabled) return true;
        router.navigate(['/dashboard']);
        return false;
      }),
      catchError(() => {
        router.navigate(['/dashboard']);
        return of(false);
      })
    );
  };
};

